import { withPagination } from 'apis/utils';
import config from 'config';
import { List, PaginatedResult, PaginationOptions } from 'types/api';
import { CompactLabelDTO, Industry } from 'types';
import { LabelDTO } from 'types/company';
import { CommunityConfiguration, CommunityVisibility, TinyCommunity } from 'types/company/community';
import { Role } from 'types/company/access';
import { TinyCompanyDTO } from 'types/company/company';
import { Location } from 'apis/MapboxAPI';
import { TinyUser, UserProfile } from 'types/user';
import { OrganizationType, TinyOrganizationDTO } from 'types/organization';
import useResource from 'util/resource/useResource';
import { put } from 'apis/ApiBase';

const baseUrl = config.COMPANIES_API_URL;

export interface Enterprise {
  id: UUID;
  slug: string;
  name: string;
  logoURL: string;
}

export interface EnterpriseCommunityMembership {
  community: TinyCommunity;
  roles: Role[];
  pendingRoles: Role[];
}

export interface EnterpriseUser {
  userprofile: UserProfile & { location?: Location };
  isAdmin: boolean;
  joinedAt?: string;
  communityMemberships: EnterpriseCommunityMembership[];
}

export interface EnterpriseCommunity {
  community: TinyCommunity;
  membersCount: number;
  administrators: TinyUser[];
  configuration?: CommunityConfiguration;
}

export function useEnterprises() {
  return useResource<List<Enterprise>>(`${baseUrl}/enterprises`);
}

export function useEnterpriseCommunities(enterpriseId: UUID) {
  return useResource<List<EnterpriseCommunity>>(`${baseUrl}/enterprises/${enterpriseId}/communities`);
}

export function useEnterpriseUsers(enterpriseId: UUID, paginationOptions: PaginationOptions) {
  return useResource<PaginatedResult<EnterpriseUser>>(
    withPagination(`${baseUrl}/enterprises/${enterpriseId}/users`, paginationOptions),
    { keepPreviousData: true },
  );
}

export const enterpriseCommunityListUrl = (enterpriseId: UUID, options?: PaginationOptions) =>
  withPagination(`${baseUrl}/enterprises/${enterpriseId}/communities`, options);

export interface EnterpriseUserCommunityAccess {
  cwUserId: UUID;
  communities: { communityId: number; roles: Role[] }[];
}

export interface EnterpriseCommunityAccessPayload {
  users: EnterpriseUserCommunityAccess[];
}

export function saveEnterpriseUserCommunityAccesses(enterpriseId: UUID, payload: EnterpriseCommunityAccessPayload) {
  return put<void>(`${baseUrl}/enterprises/${enterpriseId}/users/community-accesses`, payload);
}

export interface EnterpriseCompanyCommunityMembership {
  community: TinyCommunity;
  visibility: CommunityVisibility;
}

export interface EnterpriseCompany {
  details: TinyCompanyDTO;
  location?: Location;
  stages: CompactLabelDTO[];
  industries: Industry[];
  companyAdmin?: UserProfile;
  joinedAt?: string;
  communityMemberships: EnterpriseCompanyCommunityMembership[];
}

export interface EnterpriseCompanyFilters {
  name?: string;
  location?: string;
  productStage?: string[];
  industries?: string[];
  community?: string[];
}

export interface EnterpriseCompanyCommunityAccessPayload {
  companies: {
    companyId: number;
    communities: { communityId: number; isMember: boolean; visibility?: CommunityVisibility }[];
  }[];
}

export const enterpriseCompanyListUrl = (
  enterpriseId: UUID,
  options?: PaginationOptions,
  filters?: EnterpriseCompanyFilters & { sortBy?: string; sortOrder?: string },
) => withPagination(`${baseUrl}/enterprises/${enterpriseId}/companies`, options, filters);

export function useEnterpriseCompanies(
  enterpriseId: UUID,
  paginationOptions: PaginationOptions,
  filters?: EnterpriseCompanyFilters & { sortBy?: string; sortOrder?: string },
) {
  return useResource<PaginatedResult<EnterpriseCompany>>(
    enterpriseCompanyListUrl(enterpriseId, paginationOptions, filters),
    { keepPreviousData: true },
  );
}

export function useEnterpriseIndustries(enterpriseId: UUID) {
  return useResource<LabelDTO[]>(`${baseUrl}/enterprises/${enterpriseId}/industry-labels`);
}

export function saveEnterpriseCompanyCommunityAccesses(
  enterpriseId: UUID,
  payload: EnterpriseCompanyCommunityAccessPayload,
) {
  return put<void>(`${baseUrl}/enterprises/${enterpriseId}/companies/community-accesses`, payload);
}

export interface EnterpriseOrganizationCommunityMembership {
  community: TinyCommunity;
  visibility: CommunityVisibility;
}

export interface EnterpriseOrganization {
  details: TinyOrganizationDTO;
  location?: string;
  contactPerson?: UserProfile;
  admins: TinyUser[];
  types: OrganizationType[];
  joinedAt?: string;
  communityMemberships: EnterpriseOrganizationCommunityMembership[];
}

export interface EnterpriseOrganizationCommunityAccessPayload {
  organizations: {
    organizationId: string;
    communities: { communityId: number; isMember: boolean; visibility?: CommunityVisibility }[];
  }[];
}

export const enterpriseOrganizationListUrl = (
  enterpriseId: UUID,
  options?: PaginationOptions,
  filters?: { name?: string; location?: string; typeFilter?: string[]; community?: string[]; sortBy?: string; sortOrder?: string },
) => withPagination(`${baseUrl}/enterprises/${enterpriseId}/organizations`, options, filters);

export function saveEnterpriseOrganizationCommunityAccesses(
  enterpriseId: UUID,
  payload: EnterpriseOrganizationCommunityAccessPayload,
) {
  return put<void>(`${baseUrl}/enterprises/${enterpriseId}/organizations/community-accesses`, payload);
}
